import { useQuery } from '@apollo/client/react';
import { useEffect, useRef } from 'react';

/**
 * A wrapper hook around Apollo Client 4's useQuery that provides backward compatibility
 * with Apollo Client 3 features, specifically:
 * - onCompleted and onError callbacks
 *
 * This hook helps with migration from Apollo Client v3 to v4 by allowing code to continue
 * using the removed onCompleted and onError callbacks while gradually migrating to v4 patterns.
 *
 * In Apollo Client v3, onCompleted was called every time the query completed successfully
 * (including refetches), and onError was called every time the query encountered an error.
 * This hook replicates that behavior by watching for data/error changes.
 *
 * @template TData - The type of data returned by the query
 * @template TVariables - The type of variables for the query
 *
 * @param {import('@apollo/client').DocumentNode | import('@apollo/client').TypedDocumentNode<TData, TVariables>} query - The GraphQL query document parsed into an AST by gql
 * @param {Object} options - Options to control how the query is executed (extends useQuery.Options with onCompleted/onError)
 * @param {Function} [options.onCompleted] - Callback function called when query completes successfully. Receives the data as parameter. Called on every successful completion, including refetches.
 * @param {Function} [options.onError] - Callback function called when query encounters an error. Receives the error as parameter. Called on every error occurrence.
 * @param {TVariables} [options.variables] - Variables to pass to the query
 * @param {import('@apollo/client').DefaultContext} [options.context] - If you're using Apollo Link, this object is the initial value of the context object that's passed along your link chain
 * @param {import('@apollo/client').WatchQueryFetchPolicy} [options.fetchPolicy='cache-first'] - Specifies how the query interacts with the Apollo Client cache during execution (for example, whether it checks the cache for results before sending a request to the server)
 * @param {import('@apollo/client').WatchQueryFetchPolicy | Function} [options.nextFetchPolicy] - Specifies the FetchPolicy to be used after this query has completed
 * @param {import('@apollo/client').WatchQueryFetchPolicy} [options.initialFetchPolicy] - Defaults to the initial value of options.fetchPolicy, but can be explicitly configured to specify the WatchQueryFetchPolicy to revert back to whenever variables change (unless nextFetchPolicy intervenes)
 * @param {import('@apollo/client').RefetchWritePolicy} [options.refetchWritePolicy] - Specifies whether a NetworkStatus.refetch operation should merge incoming field data with existing data, or overwrite the existing data
 * @param {import('@apollo/client').ErrorPolicy} [options.errorPolicy='none'] - Specifies how the query handles a response that returns both GraphQL errors and partial results. The default value is 'none', meaning that the query result includes error details but not partial results
 * @param {number} [options.pollInterval=0] - Specifies the interval (in milliseconds) at which the query polls for updated results. The default value is 0 (no polling)
 * @param {boolean} [options.notifyOnNetworkStatusChange=true] - If true, the in-progress query's associated component re-renders whenever the network status changes or a network error occurs. The default value is true
 * @param {boolean} [options.returnPartialData=false] - If true, the query can return partial results from the cache if the cache doesn't contain results for all queried fields. The default value is false
 * @param {() => boolean} [options.skipPollAttempt] - A callback function that's called whenever a refetch attempt occurs while polling. If the function returns true, the refetch is skipped and not reattempted until the next poll interval
 * @param {boolean} [options.ssr] - Pass false to skip executing the query during server-side rendering
 * @param {boolean} [options.skip=false] - If true, the query is not executed. The default value is false
 * @param {import('@apollo/client').ApolloClient} [options.client] - The instance of ApolloClient to use to execute the query. By default, the instance that's passed down via context is used, but you can provide a different instance here
 * @returns {Object} Query result object matching useQuery.Result<TData, TVariables> containing:
 *   - data: TData (when available)
 *   - dataState: 'empty' | 'initial' | 'partial' | 'complete'
 *   - loading: boolean - If true, the query is still in flight
 *   - error: ErrorLike - A single ErrorLike object describing the error that occurred during the latest query execution
 *   - variables: TVariables - An object containing the variables that were provided for the query
 *   - networkStatus: NetworkStatus - A number indicating the current network state of the query's associated request
 *   - refetch: (variables?: Partial<TVariables>) => Promise<QueryResult> - A function that enables you to re-execute the query, optionally passing in new variables
 *   - fetchMore: Function - A function that helps you fetch the next set of results for a paginated list field
 *   - client: ApolloClient - The instance of Apollo Client that executed the query
 *   - observable: ObservableQuery - A reference to the internal ObservableQuery used by the hook
 *   - previousData: TData - An object containing the result from the most recent previous execution of this query
 *   - startPolling: (pollInterval: number) => void - A function that instructs the query to begin re-executing at a specified interval
 *   - stopPolling: () => void - A function that instructs the query to stop polling after a previous call to startPolling
 *   - subscribeToMore: Function - A function that enables you to execute a subscription
 *   - updateQuery: (mapFn: UpdateQueryMapFn) => void - A function that enables you to update the query's cached result without executing a followup GraphQL operation
 */
export default function useQueryWithCallbacks(query, options = {}) {
  const { onCompleted, onError, ...restOptions } = options;

  // Store callbacks in refs to avoid stale closures
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);

  // Track previous data and error to detect changes and avoid duplicate calls
  const previousDataRef = useRef(undefined);
  const previousErrorRef = useRef(undefined);
  const previousLoadingRef = useRef(true);

  // Update refs when callbacks change
  useEffect(() => {
    onCompletedRef.current = onCompleted;
    onErrorRef.current = onError;
  }, [onCompleted, onError]);

  // Use Apollo Client 4's useQuery without onCompleted/onError
  const result = useQuery(query, restOptions);

  const { data, error, loading } = result;

  // Handle onCompleted callback
  // In v3, onCompleted was called when query completed successfully (not loading and data available)
  // Apollo Client handles errorPolicy itself - we just call onCompleted when data is available
  useEffect(() => {
    // Call onCompleted when:
    // 1. Query just finished loading (was loading, now not loading)
    // 2. Data is available
    const justFinishedLoading = previousLoadingRef.current && !loading;
    const hasData = data !== undefined;

    if (
      (justFinishedLoading || (hasData && data !== previousDataRef.current)) &&
      hasData &&
      onCompletedRef.current
    ) {
      onCompletedRef.current(data);
    }

    // Update refs
    if (data !== undefined) {
      previousDataRef.current = data;
    }
    previousLoadingRef.current = loading;
  }, [data, loading]);

  // Handle onError callback
  // In v3, onError was called when query encountered an error
  useEffect(() => {
    // Call onError when:
    // 1. Query just finished loading (was loading, now not loading) OR error changed
    // 2. Error exists
    const justFinishedLoading = previousLoadingRef.current && !loading;
    const errorChanged = error && error !== previousErrorRef.current;

    if ((justFinishedLoading || errorChanged) && error && onErrorRef.current) {
      onErrorRef.current(error);
    }

    // Update refs
    if (error) {
      previousErrorRef.current = error;
    }
  }, [error, loading]);

  return result;
}